import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BookOpen, Download, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const Actions = ({ r, size = "sm" }) => (
  <div className="flex flex-wrap gap-3">
    <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 bg-[var(--accent-violet)] hover:bg-[#6d28d9] text-white rounded-full font-semibold transition-colors ${size === "lg" ? "px-6 py-3 text-sm" : "px-4 py-2 text-xs"}`} data-testid={`read-report-${r.slug}`}>
      <BookOpen className="w-4 h-4" /> Read report
    </a>
    <a href={r.pdf_url} download target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 border border-[rgba(124,58,237,0.4)] hover:border-[var(--accent-violet)] text-white rounded-full transition-colors ${size === "lg" ? "px-6 py-3 text-sm" : "px-4 py-2 text-xs"}`} data-testid={`download-report-${r.slug}`}>
      <Download className="w-4 h-4" /> Download PDF
    </a>
  </div>
);

const Meta = ({ r }) => (
  <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--accent-violet-light)] font-semibold mb-3">
    {r.series}{r.issue && ` · ${r.issue}`}
  </p>
);

const Featured = ({ r }) => (
  <motion.article initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="grid md:grid-cols-[minmax(0,380px)_1fr] gap-10 lg:gap-16 items-center mb-24" data-testid="featured-report">
    <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="block group">
      <img src={r.cover} alt={r.title} className="w-full rounded-lg shadow-2xl shadow-black/60 ring-1 ring-white/5 transition-transform duration-500 group-hover:-translate-y-1 group-hover:rotate-[-0.5deg]" />
    </a>
    <div>
      <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-[#f5c451] border border-[#f5c451]/40 rounded-full px-3 py-1 mb-6">Latest report</span>
      <Meta r={r} />
      <h2 className="font-display text-4xl lg:text-5xl font-bold text-white italic leading-tight mb-5">{r.title}</h2>
      <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed mb-4 max-w-xl">{r.summary}</p>
      <p className="text-xs text-[var(--text-secondary)]/70 mb-8">{r.date_label}{r.pages ? ` · ${r.pages} pages` : ""}</p>
      <Actions r={r} size="lg" />
    </div>
  </motion.article>
);

const Card = ({ r, i }) => (
  <motion.article initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="flex flex-col" data-testid={`report-card-${r.slug}`}>
    <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="block group mb-6">
      <img src={r.cover} alt={r.title} loading="lazy" className="w-full aspect-[3/4] object-cover object-top rounded-lg shadow-xl shadow-black/50 ring-1 ring-white/5 transition-transform duration-500 group-hover:-translate-y-1" />
    </a>
    <Meta r={r} />
    <h3 className="font-display text-2xl font-semibold text-white italic leading-snug mb-3">{r.title}</h3>
    <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-3 flex-1">{r.summary}</p>
    <p className="text-xs text-[var(--text-secondary)]/70 mb-5">{r.date_label}{r.pages ? ` · ${r.pages} pages` : ""}</p>
    <Actions r={r} />
  </motion.article>
);

const ResearchPage = () => {
  const [reports, setReports] = useState(null);
  useEffect(() => { fetch(`${API}/api/reports`).then((r) => r.json()).then(setReports).catch(() => setReports([])); }, []);
  const [first, ...rest] = reports || [];

  return (
    <main className="pt-28 pb-24 min-h-screen" data-testid="research-page">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="max-w-3xl mb-16">
          <p className="text-sm uppercase tracking-[0.25em] text-[var(--accent-violet-light)] font-medium mb-4">Research & Reports</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white italic mb-5" data-testid="research-heading">Research</h1>
          <p className="text-[var(--text-secondary)] text-base md:text-lg">Designed, consulting-grade briefings on AI, money, work and policy: what changed, why it matters, and what to do next.</p>
        </div>
        {reports && reports.length === 0 && <p className="text-[var(--text-secondary)]" data-testid="research-empty">New reports are on the way.</p>}
        {first && <Featured r={first} />}
        {rest.length > 0 && (
          <section>
            <h2 className="font-display text-3xl text-white italic mb-10">More reports</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-12">
              {rest.map((r, i) => <Card key={r._id} r={r} i={i} />)}
            </div>
          </section>
        )}
        <div className="mt-24 border-t border-[rgba(124,58,237,0.12)] pt-10 flex flex-wrap items-center justify-between gap-4">
          <p className="text-[var(--text-secondary)]">Want these briefings presented to your leadership team?</p>
          <Link to="/speaking" className="inline-flex items-center gap-2 text-white hover:text-[var(--accent-violet-light)] transition-colors" data-testid="research-briefing-cta">Book an executive briefing <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    </main>
  );
};

export default ResearchPage;
